import { Card, CardContent } from "@mui/material";
import {
  List,
  Datagrid,
  DateField,
  BooleanField,
  EditButton,
  FunctionField,
  ReferenceField,
  FilterLiveSearch,
  SavedQueriesList,
  FilterList,
  FilterListItem,
  Filter,
  ReferenceInput,
  AutocompleteInput,
} from "react-admin";
import { statusChoices, statusToChinese } from "../../utils/driver-schedule";
import DateFieldComponent from "../components/date-field-component";
import DateRangeIcon from "@mui/icons-material/DateRange";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StatusIcon from "@mui/icons-material/AssignmentTurnedIn";
import RepeatIcon from "@mui/icons-material/Repeat";
import { createTrucksFilterQuery } from "./common";
import { getTodayRange } from "../../utils/common";

const DepartureDateFilter = (props: any) => (
  <Filter {...props}>
    <DateFieldComponent
      source="departureDate"
      label="指定出发日期"
      showClearIcon={true}
      alwaysOn
    />
  </Filter>
);

const ArrivalDateFilter = (props: any) => (
  <Filter {...props}>
    <DateFieldComponent
      source="arrivalDate"
      label="指定到达日期"
      showClearIcon={true}
      alwaysOn
    />
  </Filter>
);

const DriverFilter = (props: any) => (
  <Filter {...props}>
    <ReferenceInput source="driver" reference="fleetAssignments" alwaysOn>
      <AutocompleteInput
        optionText={(option) =>
          `${option.truck.licensePlate ?? option.truck.plate} - ${
            option.driver === null ? "未知司机" : option.driver.name
          } - ${option.source ?? `线下收集`}`
        }
        label="选择司机与货车（可输入车牌号快速查找）"
        filterToQuery={createTrucksFilterQuery}
        noOptionsText="无选项"
        fullWidth
      />
    </ReferenceInput>
  </Filter>
);

export const FilterSidebar = () => {
  const todayRange = getTodayRange();
  return (
    <Card sx={{ order: -1, mr: 2, mt: 9, width: 320 }}>
      <CardContent>
        <SavedQueriesList />
        <DriverFilter />
        <FilterList label="出发日期" icon={<DateRangeIcon />}>
          <FilterListItem
            label="今天"
            value={{
              departureDate_gte: todayRange.start,
              departureDate_lte: todayRange.end,
            }}
          />
          <FilterListItem
            label="本周"
            value={{
              departureDate_gte: new Date(
                new Date().setDate(new Date().getDate() - new Date().getDay())
              ).toISOString(),
              departureDate_lte: new Date(
                new Date().setDate(
                  new Date().getDate() + (6 - new Date().getDay())
                )
              ).toISOString(),
            }}
          />
          <DepartureDateFilter />
        </FilterList>
        <FilterList label="到达日期" icon={<DateRangeIcon />}>
          <FilterListItem
            label="今天"
            value={{
              arrivalDate_gte: todayRange.start,
              arrivalDate_lte: todayRange.end,
            }}
          />
          <FilterListItem
            label="本周"
            value={{
              arrivalDate_gte: new Date(
                new Date().setDate(new Date().getDate() - new Date().getDay())
              ).toISOString(),
              arrivalDate_lte: new Date(
                new Date().setDate(
                  new Date().getDate() + (6 - new Date().getDay())
                )
              ).toISOString(),
            }}
          />
          <ArrivalDateFilter />
        </FilterList>
        <FilterList label="始发站" icon={<LocationOnIcon />}>
          <FilterLiveSearch source="origin.city" label="城市名" />
        </FilterList>
        <FilterList label="终点站" icon={<LocationOnIcon />}>
          <FilterLiveSearch source="destination.city" label="城市名" />
        </FilterList>
        <FilterList label="是否为返程" icon={<RepeatIcon />}>
          <FilterListItem label="是" value={{ isReturnTrip: true }} />
          <FilterListItem label="否" value={{ isReturnTrip: false }} />
        </FilterList>
        <FilterList label="状态" icon={<StatusIcon />}>
          {statusChoices.map((choice) => (
            <FilterListItem
              key={choice.id}
              label={choice.name}
              value={{ status: choice.id }}
            />
          ))}
        </FilterList>
      </CardContent>
    </Card>
  );
};

export const DriverScheduleList = (props: any) => (
  <List
    {...props}
    aside={<FilterSidebar />}
    sx={{
      "& .RaFilterForm-filterFormInput": {
        width: "100%",
      },
    }}
  >
    <Datagrid rowClick="show">
      <ReferenceField reference="fleetAssignments" label="司机" source="driver">
        <FunctionField
          render={(record: any) =>
            `${record.truck.licensePlate ?? record.truck.plate} - ${
              record.driver === null ? "未知司机" : record.driver.name
            } - ${record.source ?? `线下收集`}`
          }
        />
      </ReferenceField>
      <FunctionField
        label="车队"
        sortBy="driver"
        render={(record: any) => {
          const fleets =
            record.fleets?.map((fleet: any) => fleet.name).join(", ") ||
            "未知车队";
          return fleets;
        }}
      />
      <FunctionField
        label="始发站"
        sortBy="origin.province"
        render={(record: any) =>
          `${record.origin?.province || ""} ${record.origin?.city || ""} ${
            record.origin?.district || ""
          }`
        }
      />
      <FunctionField
        label="终点站"
        sortBy="destination.province"
        render={(record: any) =>
          `${record.destination?.province || ""} ${
            record.destination?.city || ""
          } ${record.destination?.district || ""}`
        }
      />
      <DateField source="departureDate" label="出发日期" />
      <DateField source="arrivalDate" label="到达日期" />
      <FunctionField
        label="状态"
        sortBy="status"
        render={(record: any) => statusToChinese(record.status)}
      />
      <BooleanField source="isReturnTrip" label="为返程路线" />
      <EditButton />
    </Datagrid>
  </List>
);
